import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useCurrentClosePeriod } from '@/hooks/queries/useCloseHub';
import { useCurrentPeriodReport } from '@/hooks/queries/useCurrentPeriodReport';
import { useFilingPreflight } from '@/hooks/queries/useFinanceReports';
import { useSortInboxQueue } from '@/hooks/queries/useSortInbox';
import { useCloseHubOnboardingState } from '@/hooks/useCloseHubOnboardingState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { GentleLoadingScreen } from '@/components/ui';
import { AddTransactionMenu } from '@/components/finance/entry';
import { cn } from '@/lib/utils';
import { ActionQueue } from './ActionQueue';
import { BoundaryStragglerAlerts } from './BoundaryStragglerAlerts';
import { NeedsAmendmentAlerts, useOpenAmendments } from '@/components/finance/shared';
import { CloseCaughtUpEmptyState } from './CloseCaughtUpEmptyState';
import { CloseOverdueNotice } from './CloseOverdueNotice';
import { ReceiptChaseAlerts } from './ReceiptChaseAlerts';
import { CloseFileRoute } from './CloseFileRoute';
import { CloseHubPreflight } from './CloseHubPreflight';
import { CloseMasthead } from './CloseMasthead';
import { InboxStrip } from './InboxStrip';
import { LedgerExclusionsNote } from './LedgerExclusionsNote';
import { OpenAmendmentCard } from './OpenAmendmentCard';
import { TriageLedger, TriageLedgerSkeleton } from './TriageLedger';
import { UpcomingFilingsStrip } from './UpcomingFilingsStrip';

/**
 * One-phase Close hub (ADR-011), treasurer redesign 2026-06-27:
 *
 * - Masthead (`CloseMasthead`): editorial header + one deadline line (due date +
 *   tone pill + inline Preview). The old Next-report-due / Upcoming-filings cards
 *   are gone; their behavior moved into the zones below.
 * - Ranked action queue (`ActionQueue`): the one "do this now" stack —
 *   overdue rapid reports → owed daily report → amendment nudge.
 * - Upcoming-filings strip (`UpcomingFilingsStrip`): the ambient, never-red list.
 * - Filing-readiness (`CloseHubPreflight`): the hard-failure list, before the
 *   File modal.
 * - A STICKY progress strip (`InboxStrip`): the segment bar + legend + the
 *   import → add → triage actions. Its primary action is "Triage N items" while
 *   work remains and flips to "Ready to file" once triage hits zero. This is the
 *   one persistent action bar — the old sticky register footer is gone, and the
 *   card carries no file CTA; "Ready to file" opens the CPH-014 File workflow.
 * - The ledger table (`TriageLedger`).
 */
export function CloseLayout() {
  const { data: period, isLoading } = useCurrentClosePeriod();
  const onboardingState = useCloseHubOnboardingState();
  const [fileOpen, setFileOpen] = useState(false);
  const amending = useOpenAmendments().length > 0;
  const openFile = () => setFileOpen(true);

  // The calculated processor-fee lines pinned into the ledger (weightless dashed
  // register rows under the in-period/owed header — they never enter the item list,
  // so no count/total/selection/gate can see them).
  //
  // Source resolution: both the live sort-inbox feed and, once a report exists, its
  // preflight lines are now scoped to the OWED period (issue #108) — the period being
  // filed — so they agree and there is no gap-window mismatch. Prefer the preflight
  // lines once a report exists (the SAME window the must-record gate reads); until then
  // the live feed shows the owed fee the moment activity is confirmed. Booking is
  // period-scoped and server-dated, so no reportId / periodEnd is threaded to the pin.
  const current = useCurrentPeriodReport();
  const reportId = current.status === 'ready' ? current.data.reportId : null;
  const { data: preflight } = useFilingPreflight(reportId);
  const { data: queue } = useSortInboxQueue();
  const processorFeeLines =
    preflight?.processorFeeLines ?? queue?.processorFeeLines ?? [];

  // Cold load (reload / first navigation): the resolver reports `loading` until
  // its queries settle. Hold a calm loader instead of flashing the no-sources
  // gate — that flash read as "everything is lost" to treasurers.
  const isLoadingOnboarding = onboardingState === 'loading';
  const isGated =
    onboardingState === 'gate:no-sources' ||
    onboardingState === 'gate:no-formation-date';

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Masthead: editorial header + the Next-report-due / Upcoming-filings
          cards. Kept visible while gated (honest empty states) so the page
          never collapses to a bare gate. */}
      <div data-slot="close-masthead">
        <CloseMasthead />
      </div>

      {/* Zone 2 — the ranked action queue. Consolidates the old alert band's
          rapid / daily / amendment surfaces plus the overdue filing rows into one
          Sonner-style stack. Kept visible while gated (honest empty states); the
          queue renders nothing when there's nothing to act on. */}
      <div data-slot="close-action-queue">
        <ActionQueue />
      </div>

      {/* Amendments the treasurer started and has not filed. Beside an open period they are the
          second item; with none open they are the page's item. Self-hides when there are none. */}
      <div data-slot="close-open-amendment-zone">
        <OpenAmendmentCard />
      </div>

      {/* Boundary-straggler alerts (#565): synced bank lines that dated into an already-closed
          period, kept out of the ledger and surfaced here with per-line detail + a deep link into
          the affected report's amend affordance. Self-hides when there are none, so it's safe to
          keep in the always-visible band (honest empty state). */}
      <div data-slot="close-straggler-alerts-zone">
        <BoundaryStragglerAlerts />
      </div>

      {/* Reports awaiting an amendment (see NeedsAmendmentAlerts). Self-hides when there are
          none, so it's safe in the always-visible band. */}
      <div data-slot="close-needs-amendment-zone">
        <NeedsAmendmentAlerts />
      </div>

      {/* Filed reports still waiting on the receiving office's receipt (see ReceiptChaseAlerts). */}
      <div data-slot="close-receipt-chase-zone">
        <ReceiptChaseAlerts />
      </div>

      {/* Zone 3 — the ambient Upcoming-filings strip (replaces the old card). One
          quiet slate line, always visible, never red. */}
      <div data-slot="close-upcoming">
        <UpcomingFilingsStrip />
      </div>

      {/* Filing readiness on the hub, BEFORE the File modal: the existing
          preflight hard-failure list (and the preflight-only blockers the badge
          can't see) surface here. CloseHubPreflight carries its own
          `data-slot="close-preflight"` and self-gates to an open period, so it
          renders nothing while gated or between periods. */}
      {!isGated && !isLoadingOnboarding && <CloseHubPreflight />}

      {isLoadingOnboarding ? (
        <div data-slot="close-loading">
          <GentleLoadingScreen
            title="Loading your close hub…"
            subtitle="Fetching your latest activity — nothing is lost."
          />
        </div>
      ) : isGated ? (
        <OnboardingGateCard state={onboardingState} />
      ) : (
        <>
          {/* The progress strip is the one sticky action bar — it pins to the
              top of the scroll area (the admin shell's <main>) so the legend +
              triage/file actions stay reachable while the ledger scrolls. Full
              bleed via -mx-6 to span the main padding, re-inset with px-6; -top-6
              likewise cancels main's top padding, which a top-0 sticky would pin
              below, leaving a strip of scrolled ledger showing above the bar. */}
          {!isLoading && period && (
            <div
              data-slot="close-strip"
              className={cn(
                'sticky -top-6 z-20 -mx-6 border-b border-slate-200 px-6 py-3',
                'bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80',
              )}
            >
              <InboxStrip onOpenFile={openFile} />
            </div>
          )}

          <div
            data-slot="close-inbox"
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-card-foreground"
          >
            {/* Still arriving: reuse the ledger's own skeleton rather than a bare "Loading…"
                line — and, critically, never the caught-up card below, which asserts something
                about the treasurer's obligations that we do not yet know. */}
            {isLoading && <TriageLedgerSkeleton />}
            {/* The strip carries Add transaction while a period is open. Without one the
                treasurer still adds transactions (the date picks the period), so the door
                moves here rather than disappearing. */}
            {!isLoading && period === null && (
              <div data-slot="close-no-period-actions" className="mb-4 flex justify-end">
                <AddTransactionMenu />
              </div>
            )}
            {/* A missed, never-filed report is owed before anything else, so it leads the
                card. The caught-up card below needs a null period, so the two never show
                together. */}
            {!isLoading && period?.isOverdue && (
              <div className="mb-6">
                <CloseOverdueNotice period={period} onFile={openFile} />
              </div>
            )}
            {/* No open period (filed early, or between periods): show the calm
                "you're all caught up" reassurance instead of a cold sentence.
                The ledger below still renders so the treasurer's history never
                looks like it vanished. An amendment in progress is still owed, so
                it withholds the card; the amendment card above carries the page. */}
            {!isLoading && period === null && !amending && (
              <div className="mb-6">
                <CloseCaughtUpEmptyState />
              </div>
            )}
            {/* The all-time ledger is NOT gated on an open period — it reads its
                own data (useSortInbox) and shows the full history whether or not
                a period is open, so it stays on screen between periods. */}
            {!isLoading && (
              <TriageLedger processorFeeLines={processorFeeLines} />
            )}
            {/* Why a bank line might not be here (#568): the treasurer-first
                explainer for the bank-pull's deliberate silent drops (untracked
                accounts + pre-onboarding history, ADR-072). Self-hides unless a
                live bank-pull makes those exclusions relevant. */}
            {!isLoading && <LedgerExclusionsNote />}
          </div>
        </>
      )}

      <Dialog open={fileOpen} onOpenChange={setFileOpen}>
        <DialogContent
          data-testid="close-file-modal"
          className="max-w-5xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>File this period</DialogTitle>
          </DialogHeader>
          <CloseFileRoute />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OnboardingGateCard({
  state,
}: {
  state: 'gate:no-sources' | 'gate:no-formation-date';
}) {
  const copy =
    state === 'gate:no-sources'
      ? {
          heading: 'Connect a source to start.',
          body: 'Link a bank account or payment processor to begin importing activity.',
          ctaLabel: 'Open Sources',
          ctaHref: '/admin/finance/settings/sources',
        }
      : {
          heading: 'Set your committee formation date.',
          body: 'We use this to align period boundaries with your filing obligations.',
          ctaLabel: 'Open Setup',
          ctaHref: '/admin/finance/settings/setup',
        };

  return (
    <div
      data-slot="close-onboarding-gate"
      data-gate-state={state}
      className={cn(
        'mx-auto flex w-full max-w-md flex-col items-center gap-3 rounded-md',
        'border border-border bg-card p-6 text-center text-card-foreground',
      )}
    >
      <h2 className="text-lg font-semibold">{copy.heading}</h2>
      <p className="text-sm text-muted-foreground">{copy.body}</p>
      <Link
        to={copy.ctaHref}
        data-testid="close-gate-cta"
        className={cn(
          'inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm',
          'font-medium text-primary-foreground hover:bg-primary/90',
        )}
      >
        {copy.ctaLabel}
      </Link>
    </div>
  );
}
